/**
 * Quote PDF builder — mirrors the layout of ELNAF's official invoice
 * (see invoice.pdf) minus the "PAY TO" / bank block, since a quotation
 * is not yet payable.
 *
 * Key layout decisions taken from the reference invoice:
 *   · SHIPPING is a table LINE ITEM, not a separate totals row.
 *   · Columns: Item | Quantity | Unit | Price | VAT % | Price incl. VAT | Total
 *   · Line totals are rounded from the raw product, not from the rounded
 *     unit-incl-VAT (2 × 29.40 × 1.255 = 73.79, not 2 × 36.90 = 73.80).
 *
 * jsPDF + autoTable are dynamically imported so ~350 kB of PDF machinery
 * stays out of the admin's main bundle.
 */

import {
  computeTotals, effectiveUnit, eur, lineTotalInclVat, round2, unitInclVat,
} from './quoteMath';

// Palette sampled from the reference invoice
const BLUE = [64, 169, 214];   // accent rules + section labels
const INK = [51, 51, 51];      // body text
const MUTED = [130, 138, 148]; // column headers / labels
const HAIR = [214, 219, 224];  // hairlines

const COMPANY = {
  name: 'ELNAF',
  street: 'Punttelintie',
  city: '48310 KOTKA',
  country: 'Finland',
  email: 'sales@elnaf.fi',
  phone: 'Phone: +358503139854',
  owners: 'Owners: Anar Ahadov',
  businessId: 'Business ID: 3477134-8',
  vatId: 'VAT ID: FI34771348',
  einvoice: 'E-invoicing address: 003734771348',
};

const LOGO_URL = `${process.env.PUBLIC_URL || ''}/elnaf-logo.svg`;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * jsPDF's built-in Helvetica is WinAnsi-encoded. Latin-1 accents (a o u e c n)
 * render fine, but anything outside it — Turkish/Azerbaijani i-dotless, s/g
 * cedilla, schwa; Polish l-stroke; Czech carons — comes out as spaced garbage
 * rather than being dropped, which is how "1001 Baki" printed as "1 0 0 1 B a k i".
 *
 * Embedding a Unicode TTF would add ~300 kB to every build, so we fold the
 * out-of-range characters onto their closest Latin-1 neighbour instead.
 *
 * Keys are \u escapes on purpose: this file must survive any editor's encoding.
 * Characters that ARE valid Latin-1 (ä a-umlaut, ç c-cedilla, …) are
 * deliberately absent — folding them would be a regression.
 */
const TRANSLIT = {
  'ı': 'i', 'İ': 'I',                 // ı İ
  'ş': 's', 'Ş': 'S',                 // ş Ş
  'ğ': 'g', 'Ğ': 'G',                 // ğ Ğ
  'ə': 'e', 'Ə': 'E',                 // ə Ə
  'ł': 'l', 'Ł': 'L',                 // ł Ł
  'ř': 'r', 'Ř': 'R',                 // ř Ř
  'ž': 'z', 'Ž': 'Z',                 // ž Ž
  'š': 's', 'Š': 'S',                 // š Š
  'č': 'c', 'Č': 'C',                 // č Č
  'ě': 'e', 'Ě': 'E',                 // ě Ě
  'ń': 'n', 'Ń': 'N',                 // ń Ń
  'ś': 's', 'Ś': 'S',                 // ś Ś
  'ź': 'z', 'Ź': 'Z',                 // ź Ź
  'ż': 'z', 'Ż': 'Z',                 // ż Ż
  'ą': 'a', 'Ą': 'A',                 // ą Ą
  'ę': 'e', 'Ę': 'E',                 // ę Ę
  'ů': 'u', 'Ů': 'U',                 // ů Ů
  'ő': 'o', 'Ő': 'O',                 // ő Ő
  'ű': 'u', 'Ű': 'U',                 // ű Ű
  'ā': 'a', 'ē': 'e', 'ī': 'i', 'ū': 'u', // ā ē ī ū
  '—': '-', '–': '-',                 // em/en dash
  '“': '"', '”': '"',                 // curly double quotes
  '‘': "'", '’': "'",                 // curly single quotes
  '…': '...',                              // ellipsis
  '€': 'EUR',                              // € (WinAnsi-only, not Latin-1)
};

const TRANSLIT_RE = new RegExp(`[${Object.keys(TRANSLIT).join('')}]`, 'g');

/** Make a string safe for jsPDF's standard fonts. Newlines survive, for notes. */
function tx(value) {
  if (value === null || value === undefined) return '';
  const s = String(value).replace(TRANSLIT_RE, (c) => TRANSLIT[c]);
  // Anything still beyond Latin-1 would render as noise, so drop it outright.
  return s.replace(/[^\n\r\t -ÿ]/g, '');
}

/**
 * Rasterise the brand SVG through an offscreen canvas. jsPDF cannot embed SVG
 * directly, and doing it at render time avoids checking a base64 blob into git.
 *
 * Memoised: the live preview rebuilds the document on every keystroke, and
 * re-fetching + re-rasterising a 117 kB SVG each time would dominate the frame.
 * Resolves to null on any failure so the header falls back to a wordmark.
 */
let logoPromise = null;

function rasteriseLogo(targetW) {
  return (async () => {
    try {
      const res = await fetch(LOGO_URL);
      if (!res.ok) return null;
      const svgText = await res.text();
      const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = url;
      });

      const ratio = (img.naturalHeight || img.height) / (img.naturalWidth || img.width);
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = Math.max(1, Math.round(targetW * ratio));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);

      return { dataUrl: canvas.toDataURL('image/png'), ratio };
    } catch {
      return null;
    }
  })();
}

function loadLogoPng(targetW = 900) {
  if (!logoPromise) logoPromise = rasteriseLogo(targetW);
  return logoPromise;
}

/** Item label styling from the invoice: uppercase, spaced, bold. */
const itemLabel = (line) => {
  const parts = [line.sku, line.brand, line.description || line.name]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  const capped = parts.length > 72 ? parts.slice(0, 71) + '...' : parts;
  return tx(capped).toUpperCase();
};

export async function buildQuoteDoc(quote) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 20;

  const rate = Number(quote.vatRate) || 0;
  const t = computeTotals(quote.lines, quote.shipping?.cost, rate);

  // ---------------------------------------------------------------
  // Header — wordmark left, logo right
  // ---------------------------------------------------------------
  doc.setTextColor(...INK);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(COMPANY.name, M, 24);
  doc.text(`Quotation #${tx(quote.quoteNo) || '-'}`, M, 30);

  const logo = await loadLogoPng();
  if (logo) {
    const w = 52;
    doc.addImage(logo.dataUrl, 'PNG', pageW - M - w, 16, w, w * logo.ratio, undefined, 'FAST');
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(106, 168, 47);
    doc.text('ELNAF', pageW - M, 30, { align: 'right' });
    doc.setTextColor(...INK);
  }

  // ---------------------------------------------------------------
  // Customer block (left) + meta grid (right)
  // ---------------------------------------------------------------
  const blockTop = 56;
  const c = quote.customer || {};
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...INK);

  const custLines = [
    c.company,
    c.contact,
    c.vatId ? `VAT ID: ${c.vatId}` : '',
    c.address,
    [c.postcode, c.city].filter(Boolean).join(' '),
    c.country,
  ].filter(Boolean).map(tx);

  const LINE_H = 5.6;
  custLines.forEach((l, i) => doc.text(l, M, blockTop + i * LINE_H));
  const custBottom = blockTop + Math.max(0, custLines.length - 1) * LINE_H;

  const metaX1 = M + 88;
  const metaX2 = M + 132;
  const metaCell = (label, value, x, yy) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(label, x, yy);
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    doc.text(tx(value) || '-', x, yy + 5.4);
  };
  metaCell('Valid until', fmtDate(quote.validUntil), metaX1, blockTop);
  metaCell('Issue date', fmtDate(quote.date), metaX2, blockTop);
  metaCell('Delivery', quote.shipping?.carrier, metaX1, blockTop + 14);
  metaCell('Reference #', quote.quoteNo, metaX2, blockTop + 14);
  const metaBottom = blockTop + 14 + 5.4;

  // ---------------------------------------------------------------
  // Subject line — must clear BOTH columns, however tall each grew
  // ---------------------------------------------------------------
  const subjectY = Math.max(custBottom, metaBottom) + 12;
  const subject =
    quote.subject ||
    (quote.lines.length === 1
      ? [quote.lines[0].sku, quote.lines[0].brand, quote.lines[0].description].filter(Boolean).join(' ')
      : `Quotation - ${quote.lines.length} item${quote.lines.length === 1 ? '' : 's'}`);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13.5);
  doc.setTextColor(...INK);
  doc.text(doc.splitTextToSize(tx(subject), pageW - M * 2)[0], M, subjectY);

  // ---------------------------------------------------------------
  // Items table — shipping is a line item, exactly like the invoice
  // ---------------------------------------------------------------
  const rows = quote.lines.map((l) => [
    itemLabel(l),
    String(l.qty),
    'pieces',
    eur(effectiveUnit(l)),
    `${rate}%`,
    eur(unitInclVat(l, rate)),
    eur(lineTotalInclVat(l, rate)),
  ]);

  if (t.shipping > 0) {
    const shipUnitIncl = round2(t.shipping * (1 + rate / 100));
    rows.push([
      `SHIPPING${quote.shipping?.carrier ? ` (${tx(quote.shipping.carrier).toUpperCase()})` : ''}`,
      '1',
      'pieces',
      eur(t.shipping),
      `${rate}%`,
      eur(shipUnitIncl),
      eur(shipUnitIncl),
    ]);
  }

  // Widths sum to the 170 mm content box. Right padding keeps the numeric
  // columns from touching their neighbour; the last one stays flush with the
  // margin so it lines up with the totals block underneath.
  const PAD = { top: 3.4, bottom: 3.4, left: 0, right: 3 };
  autoTable(doc, {
    startY: subjectY + 8,
    head: [['Item', 'Quantity', 'Unit', 'Price', 'VAT %', 'Price incl. VAT', 'Total']],
    body: rows,
    theme: 'plain',
    margin: { left: M, right: M },
    styles: { fontSize: 8, cellPadding: PAD, textColor: INK, overflow: 'linebreak' },
    headStyles: { fontSize: 7.5, textColor: MUTED, fontStyle: 'normal', cellPadding: { top: 0, bottom: 3, left: 0, right: 3 } },
    bodyStyles: { lineColor: HAIR, lineWidth: { bottom: 0.15 } },
    columnStyles: {
      0: { cellWidth: 54, fontStyle: 'bold', fontSize: 7.2 },
      1: { cellWidth: 16, halign: 'right' },
      2: { cellWidth: 14, halign: 'left', cellPadding: { ...PAD, left: 3, right: 0 } },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 13, halign: 'right' },
      5: { cellWidth: 26, halign: 'right' },
      6: { cellWidth: 25, halign: 'right', cellPadding: { ...PAD, right: 0 } },
    },
    didParseCell: (d) => {
      if (d.section !== 'head') return;
      d.cell.styles.halign = d.column.index === 0 || d.column.index === 2 ? 'left' : 'right';
    },
  });

  // ---------------------------------------------------------------
  // Totals — right aligned under a blue rule
  // ---------------------------------------------------------------
  let ty = doc.lastAutoTable.finalY + 1;
  if (ty > pageH - 60) { doc.addPage(); ty = 30; }

  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.7);
  doc.line(M, ty, pageW - M, ty);
  ty += 8;

  const totalRow = (label, value, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 10 : 9.2);
    doc.setTextColor(...INK);
    doc.text(label, pageW - M - 34, ty, { align: 'right' });
    doc.text(value, pageW - M, ty, { align: 'right' });
    ty += 6.6;
  };
  totalRow('Net total', eur(t.netTotal));
  totalRow(`VAT ${rate}%`, eur(t.vatAmount));
  totalRow('Total', eur(t.total), true);

  if (rate === 0) {
    ty += 1;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text('VAT 0% - reverse charge, Art. 44 EU VAT Directive.', pageW - M, ty, { align: 'right' });
  }

  // ---------------------------------------------------------------
  // Notes (left, under the totals block)
  // ---------------------------------------------------------------
  if (quote.notes) {
    let ny = doc.lastAutoTable.finalY + 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...BLUE);
    doc.text('NOTES', M, ny);
    ny += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...INK);
    doc.text(doc.splitTextToSize(tx(quote.notes), 100), M, ny);
  }

  // ---------------------------------------------------------------
  // Footer on every page — company + IDs (no bank / PAY TO block).
  //
  // Layout: a disclaimer line + page number sit on their own row directly
  // under the rule; the two company-info columns go BELOW them. Previously the
  // right-aligned disclaimer shared the same rows as the columns and overlapped
  // "Owners: Anar Ahadov".
  // ---------------------------------------------------------------
  const pages = doc.internal.getNumberOfPages();
  const rule = pageH - 30;
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.2);
    doc.line(M, rule, pageW - M, rule);

    doc.setFontSize(6.6);
    doc.setTextColor(...MUTED);

    // Row 1: disclaimer (left) + page number (right) — nothing else on this line
    doc.setFont('helvetica', 'italic');
    doc.text('This quotation is non-binding and valid until the date shown above. Prices in EUR.', M, rule + 4.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`${p}/${pages}`, pageW - M, rule + 4.5, { align: 'right' });

    // Rows below: company info in two columns, well clear of the disclaimer
    const infoTop = rule + 10;
    const col1 = [COMPANY.name, COMPANY.street, COMPANY.city, COMPANY.country, COMPANY.email, COMPANY.phone];
    const col2 = [COMPANY.owners, COMPANY.businessId, COMPANY.vatId, COMPANY.einvoice];
    col1.forEach((l, i) => doc.text(l, M, infoTop + i * 2.7));
    col2.forEach((l, i) => doc.text(l, M + 70, infoTop + i * 2.7));
  }

  return doc;
}

export function quoteFilename(quote) {
  const base = quote?.quoteNo ? `quote-${quote.quoteNo}` : 'quote';
  return `${base}.pdf`.replace(/[^A-Za-z0-9._-]/g, '_');
}

export async function downloadQuotePdf(quote) {
  const doc = await buildQuoteDoc(quote);
  doc.save(quoteFilename(quote));
}

/** Base64 payload (no data: prefix) for api/b2b_quote.php. */
export async function quotePdfBase64(quote) {
  const doc = await buildQuoteDoc(quote);
  const dataUri = doc.output('datauristring');
  return dataUri.slice(dataUri.indexOf(',') + 1);
}

/** Object URL for the preview iframe. Caller must revokeObjectURL. */
export async function quotePdfBlobUrl(quote) {
  const doc = await buildQuoteDoc(quote);
  return URL.createObjectURL(doc.output('blob'));
}
